import { useAuth0 } from '@auth0/auth0-react'
import { GButton } from 'components/basic-blocks/'
import { GDropdown } from 'components/basic-blocks/g-dropdown-menu'
import { useStores } from 'hooks'
import { AccountSwitcher } from 'layout/sidebar/menus/account-switcher'
import { AiOutlinePlus } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'
import { useUiStore } from 'store'
import { MenuLink } from '../menu-link'
import { removeFromLocalStorage } from '../../../utils'
import DoubleChevron from '../../../assets/img/chevron-selector-vertical.svg'
import { UserAvatar } from '../../../components/user-avatar/user-avatar'
import { clearMarketingParams } from '../../../utils/marketing-params'
import { clearFiltersFromStorage } from '../../../utils/filter-presistence'

export const UserHeaderDropdown = (props: {
	removeImpersonationId: () => void
}) => {
	const { removeImpersonationId } = props

	return (
		<GDropdown position="top" menuButton={<MenuButton />}>
			{(close?: () => void) => (
				<UserMenuContent
					close={close}
					removeImpersonationId={removeImpersonationId}
				/>
			)}
		</GDropdown>
	)
}

const UserMenuContent = (props: {
	close?: () => void
	removeImpersonationId: () => void
}) => {
	const { close, removeImpersonationId } = props
	const { logout } = useAuth0()
	const { userStore, accountStore, billingStore } = useStores()
	const { account, accounts, userAccess } = accountStore
	const { user } = userStore
	const navigate = useNavigate()

	const showNewWorkspaceOffers = billingStore.offers?.length > 0

	return (
		<div
			className={
				'ml-5 shadow-dropShadow mb-7 w-60 px-2 whitespace-nowrap text-ellipsis rounded-3xl  bg-popover divide-y divide-card-border'
			}
		>
			{account ? (
				<div className="py-1">
					<div className="pt-3 pb-2">
						<AccountSwitcher onCreateWorkspace={close} />
					</div>
				</div>
			) : accounts?.length > 0 ? (
				<div className="py-1">
					<AccountSwitcher onCreateWorkspace={close} />
				</div>
			) : (
				<div className="py-1">
					<div className="pt-3 pb-2">
						<GButton
							className="w-full"
							color="white"
							variant="contained"
							size="md"
							icon={AiOutlinePlus}
							iconPlacement="left"
							onClick={() => navigate('/start/standard')}
						>
							Create new workspace
						</GButton>
					</div>
				</div>
			)}
			<div className="py-1">
				<div className="flex items-center space-x-2 px-2 pt-4 pb-2">
					<UserAvatar user={user} size={8} />
					<div className="text-left flex-col flex-1 hidden md:block">
						<p className="text-base text-t-check-slate font-medium font-inter w-32 truncate">
							{user?.name}
						</p>
						<p className="text-sm text-t-profile-text font-normal font-inter w-32 truncate">
							{user?.email}
						</p>
					</div>
				</div>
				<>
					<MenuLink
						label="Add members"
						disabled={!account || account.id === 'workspace-demo'}
						to={() => `/settings/workspace/${account?.id}/team-access`}
						onClickWithNavigation={close}
					/>
					<MenuLink
						label={
							<div className="flex gap-2 items-center">
								<>
									<span>Offers</span>
									{showNewWorkspaceOffers && (
										<span className="bg-red-100 px-2.5 h-[23px] rounded-md text-red-700">
											!
										</span>
									)}
								</>
							</div>
						}
						to={() => 'settings/user/offers'}
						onClickWithNavigation={close}
					/>
					<MenuLink
						label="Notifications"
						to={() => '/settings/user/notification'}
						onClickWithNavigation={close}
					/>
				</>
			</div>
			{/* user section end */}
			{user?.admin && (
				<div className="py-1">
					<MenuLink
						label="Admin Panel"
						to={() => '/admin'}
						// icon={RiShieldKeyholeFill}
						onClickWithNavigation={() => {
							accountStore.clearAccount()
							close && close()
						}}
					/>
				</div>
			)}

			<div className="py-2 ">
				<MenuLink
					label="Logout"
					to={() => '#'}
					onClickWithoutNavigation={() => {
						removeFromLocalStorage(['selected_account', 'selected_asset'])
						clearFiltersFromStorage()
						clearMarketingParams()
						logout()
						removeImpersonationId()
					}}
				/>
			</div>
		</div>
	)
}

const MenuButton = () => {
	const { userStore, accountStore } = useStores()
	const { user } = userStore
	const { account } = accountStore
	const userAccessInfo =
		account?.user_access &&
		account.user_access.find((userAccess) => userAccess.user_id === user?.id)
	const text =
		userAccessInfo?.access_level === 'READ'
			? 'Analyst'
			: userAccessInfo?.access_level === 'WRITE'
			? 'Manager'
			: userAccessInfo?.access_level === 'OWNER'
			? 'Owner'
			: 'Other'
	const color =
		userAccessInfo?.access_level === 'READ'
			? 'green'
			: userAccessInfo?.access_level === 'WRITE'
			? 'purple'
			: userAccessInfo?.access_level === 'OWNER'
			? 'primary'
			: 'primary'
	const desktopSidebarCollapsed = useUiStore((s) => s.desktopSidebarCollapsed)
	const context = useUiStore((s) => s.context)
	return (
		<div
			className={`flex max-w-full ${
				desktopSidebarCollapsed && 'justify-center'
			} items-center cursor-pointer space-x-2`}
		>
			<div className="flex  items-center gap-2 justify-center rounded-full">
				<UserAvatar user={user} size={8} />
			</div>
			{!desktopSidebarCollapsed && (
				<div className="text-left items-center gap-2 hidden md:flex">
					<div>
						<p className="font-medium font-inter text-t-check-slate max-w-[120px] tracking-[0] leading-tight text-md truncate">
							{context !== 'admin' && account?.name
								? account?.name.replace(
										/http(s)?(:)?(\/\/)?|(\/\/)?(www\.)?/g,
										''
								  )
								: user?.name}
						</p>
						<p className="font-inter text-t-profile-text leading-tight text-sm max-w-[120px] truncate">
							{context !== 'admin' && user?.email}
						</p>
					</div>
					<img src={DoubleChevron} alt="chevron" className="w-4 h-4" />
				</div>
			)}
		</div>
	)
}
